import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  styled,
  TextField,
  Autocomplete,
  SelectChangeEvent,
} from '@mui/material';
import { fetchClients } from '../api/features/clientData/clientApi';
import { jobActivityLogApi } from '../api/features/jobActivityLog/jobActivityApi';
import type { Client } from '../api/features/clientData/client.types';
import type { JobActivityLogData } from '../api/features/jobActivityLog/jobActivity.types';
import { SAPIconButton, PageHeader } from '../components';
import { logger } from '../utils/logger';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS } from '../theme/sapDesignSystem';

// SAP Fiori Job Activity Log Components - matching other pages exactly

const SAPFilterCard = styled(Card)(({ theme }) => ({
  marginBottom: SAP_SPACING.lg,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
}));

const SAPTableHeader = styled(TableHead)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapGray1,
  '& .MuiTableCell-head': {
    fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    color: SAP_COLORS.sapGray6,
    borderBottom: `2px solid ${SAP_COLORS.sapBlue}`,
  },
}));

const ROWS_PER_PAGE = 10;

export default function JobActivityLog() {
  const { clientCode } = useClientCode();
  const isAdmin = clientCode === 'admin';
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [jobName, setJobName] = useState('');
  const [logs, setLogs] = useState<JobActivityLogData[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Fetch clients for dropdown (admin only)
  useEffect(() => {
    if (!isAdmin) return;
    const fetchClientOptions = async () => {
      try {
        const data = await fetchClients({ clientCode, firstResult: -1, maxResult: 0 });
        setClients(data);
      } catch (err) {
        logger.error('Failed to fetch clients:', err);
        setClients([]);
      }
    };

    fetchClientOptions();
  }, [clientCode, isAdmin]);

  // Fetch logs and count
  const fetchData = async (pageOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      const firstResult = (pageOverride ?? page) * ROWS_PER_PAGE;
      const params: any = {
        clientCode,
        firstResult,
        maxResult: ROWS_PER_PAGE,
      };
      if (isAdmin && selectedClient?.value) params.clientId = selectedClient.value;
      if (jobName) params.jobName = jobName;
      
      const result = await jobActivityLogApi.fetchWithCount(params);
      setLogs(result.data);
      setCount(result.total);
    } catch (err: any) {
      setError('Failed to load job activity logs');
      setLogs([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
    fetchData(0);
  };

  const handleReset = () => {
    setSelectedClient(null);
    setJobName('');
    setPage(0);
    setLogs([]);
    setCount(0);
    setError(null);
    setHasSearched(false);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
    fetchData(newPage);
  };

  const clientOptions = clients.map((client) => ({ label: client.name, value: client.id }));

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Page Header */}
      <PageHeader 
        title="Job Activity Log"
        description="View and filter job activity logs for your clients"
      />

      {/* SAP Fiori Filter Card - exactly like other pages */}
      <SAPFilterCard>
        <CardContent sx={{ p: SAP_SPACING.lg }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr auto' }, 
            gap: SAP_SPACING.md,
            alignItems: 'end'
          }}>
            {/* Client Name Autocomplete - admin only */}
            {isAdmin && (
              <Box>
                <Autocomplete
                  options={clientOptions}
                  value={selectedClient}
                  onChange={(_, v) => setSelectedClient(v)}
                  getOptionLabel={(option) => option?.label || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client Name"
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: SAP_COLORS.sapWhite,
                          '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
                          '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
                          '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
                        },
                        '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                        '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
                      }}
                    />
                  )}
                  disabled={loading}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                />
              </Box>
            )}
            
            {/* Job Name Input */}
            <TextField
              label="Job Name"
              value={jobName}
              onChange={(e) => setJobName(e.target.value)}
              placeholder="Enter job name"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: SAP_COLORS.sapWhite,
                  '& fieldset': { borderColor: SAP_COLORS.sapGray3 },
                  '&:hover fieldset': { borderColor: SAP_COLORS.sapBlue },
                  '&.Mui-focused fieldset': { borderColor: SAP_COLORS.sapBlue },
                },
                '& .MuiInputLabel-root': { color: SAP_COLORS.sapGray5 },
                '& .MuiInputBase-input': { color: SAP_COLORS.sapGray7 },
              }}
            />
            
            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' }
            }}>
              <SAPIconButton
                icon="search"
                variant="primary"
                onClick={handleSearch}
                disabled={loading}
                aria-label="Search"
              />
              <SAPIconButton
                icon="reset"
                variant="secondary"
                onClick={handleReset}
                disabled={loading}
                aria-label="Reset"
              />
            </Box>
          </Box>
        </CardContent>
      </SAPFilterCard>

      {/* SAP Fiori Data Card - exactly like other pages */}
      <SAPDataCard>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <CircularProgress sx={{ color: SAP_COLORS.sapBlue }} size={48} />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Alert severity="error" sx={{ 
                borderRadius: '8px',
                backgroundColor: SAP_COLORS.sapRed + '10',
                color: SAP_COLORS.sapRed,
                border: `1px solid ${SAP_COLORS.sapRed}30`,
              }}>
                {error}
              </Alert>
            </Box>
          ) : !hasSearched ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
              <Typography variant="body1" sx={{ color: SAP_COLORS.sapGray5 }}>
                Please use the search filters above to view job activity logs.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  {/* SAP Fiori Table Header */}
                  <SAPTableHeader>
                    <TableRow>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        width: '80px'
                      }}>
                        S.No.
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Job Name
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Last Configured Start Time
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Last Configured End Time
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Last Update By
                      </TableCell>
                      <TableCell sx={{ 
                        color: SAP_COLORS.sapGray6, 
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold 
                      }}>
                        Last Updated Date & Time
                      </TableCell>
                    </TableRow>
                  </SAPTableHeader>
                  
                  {/* Table Body */}
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ 
                          color: SAP_COLORS.sapGray5,
                          py: SAP_SPACING.xl,
                          fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                        }}>
                          No job activity logs found. Try searching with different criteria.
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((row, idx) => (
                        <TableRow
                          key={row.id || idx}
                          sx={{
                            bgcolor: idx % 2 === 0 ? SAP_COLORS.sapWhite : SAP_COLORS.sapGray1,
                            '&:hover': { 
                              bgcolor: SAP_COLORS.hover,
                            },
                            transition: 'background-color 0.2s ease',
                          }}
                        >
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray6, 
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium 
                          }}>
                            {page * ROWS_PER_PAGE + idx + 1}
                          </TableCell>
                          <TableCell sx={{ 
                            color: SAP_COLORS.sapGray7,
                            fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                          }}>
                            {row.jobName || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.formattedLastConfiguredStart || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.formattedLastConfiguredEnd || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.lastUpdateBy || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: SAP_COLORS.sapGray6 }}>
                            {row.formattedLastUpdated || 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {logs.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + logs.length, count)} of {count} job activities
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(count / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handleChangePage(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </SAPDataCard>
    </Box>
  );
}